import { useState } from 'react';

interface Service {
  id: number;
  title: string;
  short_description: string;
  icon: string;
  order: number;
}

interface ServicesEditorProps {
  data: Service[];
  onChange: (data: Service[]) => void;
}

export default function ServicesEditor({ data, onChange }: ServicesEditorProps) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Service | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const handleEdit = (service: Service) => {
    setEditingId(service.id);
    setEditForm({ ...service });
    setShowAddForm(false);
  };

  const handleSave = () => {
    if (!editForm) return;
    
    const updatedData = data.map((s) => (s.id === editForm.id ? editForm : s));
    onChange(updatedData);
    setEditingId(null);
    setEditForm(null);
  };

  const handleDelete = (id: number) => {
    if (confirm('¿Estás seguro de eliminar este servicio?')) {
      onChange(data.filter((s) => s.id !== id));
    }
  };

  const handleAdd = () => {
    const newService: Service = {
      id: Math.max(...data.map((s) => s.id), 0) + 1,
      title: '',
      short_description: '',
      icon: 'ri-service-line',
      order: data.length + 1,
    };
    setEditForm(newService);
    setShowAddForm(true);
    setEditingId(null);
  };

  const handleAddSave = () => {
    if (!editForm) return;
    onChange([...data, editForm]);
    setShowAddForm(false);
    setEditForm(null);
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newData = [...data];
    [newData[index], newData[index - 1]] = [newData[index - 1], newData[index]];
    newData.forEach((item, idx) => {
      item.order = idx + 1;
    });
    onChange(newData);
  };

  const moveDown = (index: number) => {
    if (index === data.length - 1) return;
    const newData = [...data];
    [newData[index], newData[index + 1]] = [newData[index + 1], newData[index]];
    newData.forEach((item, idx) => {
      item.order = idx + 1;
    });
    onChange(newData);
  };

  return (
    <div className="space-y-6">
      <div className="border-b pb-4 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <i className="ri-service-line text-blue-600"></i>
            Gestionar Servicios
          </h2>
          <p className="text-gray-600 mt-1">Agregar, editar o eliminar servicios</p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Agregar Servicio
        </button>
      </div>

      {(showAddForm || editingId !== null) && editForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {showAddForm ? 'Nuevo Servicio' : 'Editar Servicio'}
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: Marketing Digital"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
              <textarea
                value={editForm.short_description}
                onChange={(e) => setEditForm({ ...editForm, short_description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Descripción breve del servicio"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Icono (Remix Icon)
              </label>
              <input
                type="text"
                value={editForm.icon}
                onChange={(e) => setEditForm({ ...editForm, icon: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="ri-service-line"
              />
              <p className="text-sm text-gray-500 mt-1">
                Ver iconos en:{' '}
                <a
                  href="https://remixicon.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  remixicon.com
                </a>
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={showAddForm ? handleAddSave : handleSave}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-save-line mr-2"></i>
                Guardar
              </button>
              <button
                onClick={() => {
                  setEditingId(null);
                  setEditForm(null);
                  setShowAddForm(false);
                }}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {data.map((service, index) => (
          <div
            key={service.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className={`${service.icon} text-2xl text-blue-600`}></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{service.title}</h3>
                  <p className="text-gray-600 text-sm mt-1">{service.short_description}</p>
                  <p className="text-xs text-gray-400 mt-2">Orden: {service.order}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Mover arriba"
                >
                  <i className="ri-arrow-up-line"></i>
                </button>
                <button
                  onClick={() => moveDown(index)}
                  disabled={index === data.length - 1}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Mover abajo"
                >
                  <i className="ri-arrow-down-line"></i>
                </button>
                <button
                  onClick={() => handleEdit(service)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  title="Editar"
                >
                  <i className="ri-edit-line"></i>
                </button>
                <button
                  onClick={() => handleDelete(service.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  title="Eliminar"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
